import { faRightFromBracket, faUser, faUserTie } from '@fortawesome/pro-solid-svg-icons';
import React, { useContext, useEffect, useState } from 'react';
import { OrganisationResponseItem } from '../../../Redux/Api/Request/Organisations';
import Button from '../Form/Button';
import { logout } from "../../../Redux/State/Auth";
import Dropdown from '../Form/Dropdown';
import { useLazyGetOrganisationQuery, useLazyGetOrganisationsQuery } from '../../../Redux/Api/Organisations';
import { JWT } from '../../../Utils/JWT';
import { setOrganisation } from '../../../Redux/State/Organisation';
import { useAppDispatch, useAppSelector } from '../../../Redux/store';
import { useNavigate } from 'react-router-dom';
import { ModalContext } from '../Modal/Modal';
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface SidebarUserProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    onVisibilityChange: (visibilityMode: boolean) => void;
    orgLogo: string;
}

const SidebarUser = (props: SidebarUserProps) => {
    const {
        children,
        orgLogo,
        onVisibilityChange,
        ...restProps
    } = props;
    const org_id = useAppSelector((s) => s.organisation.id);
    const org_name = useAppSelector((s) => s.organisation.name);
    const { t, i18n } = useTranslation();
    const modal = useContext(ModalContext);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const full_name = JWT.read<string>('given_name') ? JWT.read<string>('given_name')! + ' ' + JWT.read('family_name')! : '';
    const [getOrganisations, { data: organisations }] = useLazyGetOrganisationsQuery();
    const [getOrganisation] = useLazyGetOrganisationQuery();
    const hidden = { hidden: 'true' };
    const [visibilityMode, setVisibilityMode] = useState(false);

    const changeVisibility = () => {
        const newVisibility = !visibilityMode;
        setVisibilityMode(newVisibility);
        if (onVisibilityChange) {
            onVisibilityChange(newVisibility);
        }
    };

    useEffect(() => {
        if (!JWT.read('organisation_id')) getOrganisations(hidden);
        else setFirstOrg(JWT.read('organisation_id')!);
    }, []);

    useEffect(() => {
        if (!organisations) return;
        let organisationId = organisations![0].id;
        if (localStorage) {
            const checkOrganisation = localStorage.getItem('active_organisation');
            if (checkOrganisation) {
                organisationId = checkOrganisation;
            }
        }
        if (!org_id) setFirstOrg(organisationId);
    }, [organisations]);

    const setFirstOrg = (id: string) => {
        if (id === org_id) return;
        localStorage.setItem('active_organisation', id);
        getOrganisation({ id }).then((data) => {
            if (org_id) navigate(`/${i18n.language}/dashboard`);
            if (!data.data) return;

            dispatch(setOrganisation(data.data));
        });
    };

    const setOrg = async (id: string) => {
        if (id === org_id) return;
        if (!(await modal.show(t('admin:switchOrganizationWarningTitle'), t('admin:switchOrganizationWarningMessage')))) {
            modal.hide();
            return;
        }
        modal.hide();
        localStorage.setItem('active_organisation', id);

        window.location.reload();
    };

    const handleLogout = () => {
        dispatch(logout());
        navigate(`/${i18n.language}/login`);
    };

    return (
        <>
            <div {...props} className='flex flex-col mt-auto px-5 pb-5 relative'>

                {(!visibilityMode && organisations) && (
                    <div className='flex flex-col'>
                        <Dropdown
                            className="mt-2"
                            noMarginBottom
                            up
                            large
                            onChange={setOrg}
                            initial={org_id}
                            label={t('sidebar:organisations')}
                            options={Object.fromEntries(
                                organisations.map((org) => [org.id, org.name])
                            )}
                        />
                    </div>
                )}

                <div className='flex items-center justify-between'>
                    <div className='flex flex-col w-[60vw] sm:w-[220px] lg:w-[170px]'>
                        <div className='flex text-lg'>
                            {org_name
                                ? <p className='whitespace-nowrap overflow-hidden text-ellipsis' title={org_name}>{org_name}</p>
                                : <span className='bg-gray-100 text-gray-100 rounded-[0.6rem] disable-select animate-pulse'>Organizatie filler</span>
                            }
                        </div>
                        <div className='flex gap-2 items-center'>
                            {organisations ? (
                                <FontAwesomeIcon
                                    tabIndex={0}
                                    onClick={changeVisibility}
                                    onKeyDown={(e) => { if (e.key === 'Enter') changeVisibility() }}
                                    icon={visibilityMode ? faUser : faUserTie}
                                    className='opacity-60 cursor-pointer hover:text-brand transition-colors'
                                />
                            ) : (
                                <FontAwesomeIcon icon={faUser} className='opacity-60' />
                            )}
                            {full_name
                                ? <p className='whitespace-nowrap overflow-hidden text-ellipsis opacity-60'>{full_name}</p>
                                : <span className='bg-gray-100 text-gray-100 rounded-[0.6rem] disable-select animate-pulse delay-100'>Naam filler</span>
                            }
                        </div>
                    </div>
                    <div className='flex items-center gap-3'>
                        {orgLogo && <img className='w-[50px] h-[50px] object-contain' src={orgLogo} alt={org_name} />}
                        <Button
                            title={t('sidebar:logout')}
                            className='bg-brand hover:bg-brand-light min-h-[40px] max-h-[40px] min-w-[40px] max-w-[40px]'
                            action={modal.withModal({ title: t('modal:title'), body: t('modal:logout') }, handleLogout)}
                            icon={faRightFromBracket}
                            noTitle
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default SidebarUser;
